import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Forms } from '@air/api';
import { type Form, type FormsListResponse, type UpdateFormInput } from '@air/api/types';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getContentCollectionFormsKey } from './useListForms';

export const useUpdateForm = () => {
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const { showToast } = useToasts();

  const updateForm = useCallback(
    async (params: UpdateFormInput & Pick<Form, 'boardId'>) => {
      const updateCache = (formsData: FormsListResponse | undefined) => {
        if (!formsData) {
          return;
        }
        return {
          ...formsData,
          data: params ? ([params] as Form[]) : formsData.data,
        };
      };

      if (!workspaceId) {
        throw new Error('Workspace ID is not defined');
      }

      const queryKey = getContentCollectionFormsKey(params.boardId);
      try {
        queryClient.setQueryData<FormsListResponse | undefined>(queryKey, updateCache);
        await Forms.updateForm({
          workspaceId,
          form: params,
        });
      } catch (error) {
        reportErrorToBugsnag({
          error,
          context: 'Failed to update content collection form',
          metadata: { data: { params } },
        });
        showToast('Failed to update form, please try again.', { color: 'red' });
      } finally {
        queryClient.invalidateQueries({ queryKey });
      }
    },
    [queryClient, workspaceId, showToast],
  );

  return { updateForm };
};
